import { getColor } from '@/helpers/utils';
import { FaCircle, FaSquare, FaRegCircle } from 'react-icons/fa';
import { RiTriangleFill } from 'react-icons/ri';
import MarkdownRenderer from './MarkdownRenderer';
import type { LighthouseAudit } from '@/app/PageSpeed/api/types/analysisApiTypes';
import type { ReactNode } from 'react';

type Props = {
  audit: LighthouseAudit;
  direction?: 'rtl' | 'ltr';
};

const ScoreDisplayRenderer = ({ audit, direction = 'ltr' }: Props) => {
  return (
    <div className="d-flex align-items-center gap-1">
      {scoreBeforeTitle(audit)}
      <div
        style={{ direction }}
        className="fs-sm--1 fs--2 text-1000"
      >
        <MarkdownRenderer content={audit.title} />
        <div style={{ color: colorByScore(audit.score) }}>
          {audit.explanation ? audit.explanation : ''}
        </div>
      </div>
    </div>
  );
};

export default ScoreDisplayRenderer;

export const scoreBeforeTitle = (audit: LighthouseAudit): ReactNode => {
  switch (audit.scoreDisplayMode) {
    case 'metricSavings':
      return (
        <div
          style={{ color: colorByScore(audit.score) }}
          className="d-flex fs--1 align-items-center gap-1"
        >
          {iconByScore(audit.score)}
          {audit.displayValue && `${audit.displayValue} `}
        </div>
      );
    case 'binary':
      return (
        <div
          style={{ color: colorByScore(audit.score) }}
          className="d-flex fs--1 align-items-center gap-1"
        >
          {iconByScore(audit.score)}
          {audit.displayValue && `${audit.displayValue} `}
        </div>
      );
    case 'numeric':
      return (
        <div
          style={{ color: colorByScore(audit.score) }}
          className="d-flex fs--1 align-items-center gap-1"
        >
          {iconByScore(audit.score)}
          {audit.displayValue}
        </div>
      );
    case 'informative':
      return (
        <div className="d-flex fs--1 text-secondary align-items-center gap-1">
          <FaRegCircle
            size={14}
            className="ms-2"
            color={getColor('secondary')}
          />
          {audit.displayValue}
        </div>
      );
    case 'manual':
      return (
        <div className="d-flex fs--1 text-secondary align-items-center gap-1">
          <FaRegCircle
            size={14}
            className="ms-2"
            color={getColor('secondary')}
          />
        </div>
      );
    case 'notApplicable':
      return (
        <div className="d-flex fs--1 text-secondary align-items-center gap-1">
          <FaRegCircle
            size={14}
            className="ms-2"
            color={getColor('secondary')}
          />
        </div>
      );
    default:
      return <></>;
  }
};

export const colorByScore = (score?: number | null): string => {
  if (score != null && score >= 0.9) {
    return getColor('success');
  } else if (score != null && score < 0.9 && score >= 0.5) {
    return getColor('warning');
  } else {
    return getColor('danger');
  }
};

export const iconByScore = (score?: number | null): ReactNode => {
  if (score != null && score >= 0.9) {
    return <FaCircle size={14} className="ms-2" color="inherit" />;
  } else if (score != null && score < 0.9 && score >= 0.5) {
    return <FaSquare size={14} className="ms-2" color="inherit" />;
  } else {
    return (
      <RiTriangleFill
        size={14}
        className="ms-2"
        style={{ transform: 'rotate(180deg)' }}
        color="inherit"
      />
    );
  }
};
